import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './AuthLogs.css';

interface AuthLog {
  id: string;
  event: string;
  email: string;
  userId?: string;
  timestamp: Date;
  details?: any;
  error?: string;
}

const AuthLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuthLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuthLog | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');
      
      const logsRef = collection(db, 'authLogs');
      const q = query(logsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const logsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          event: data.event,
          email: data.email,
          userId: data.userId,
          timestamp: data.timestamp?.toDate() || new Date(),
          details: data.details,
          error: data.error
        };
      });
      
      setLogs(logsData);
    } catch (err) {
      console.error('Error fetching auth logs:', err);
      setError('Failed to load authentication logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.event.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.userId && log.userId.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesDateRange = 
      (!dateRange.start || log.timestamp >= dateRange.start) &&
      (!dateRange.end || log.timestamp <= dateRange.end);
    
    return matchesSearch && matchesDateRange;
  });

  const formatDate = (date: Date) => {
    return date.toLocaleString();
  };

  const formatDetails = (details: any) => {
    if (!details) return 'No details available';
    return JSON.stringify(details, null, 2);
  };

  return (
    <div className="auth-logs-container">
      <div className="auth-logs-header">
        <h1>Authentication Logs</h1>
        <div className="auth-logs-controls">
          <input
            type="text"
            placeholder="Search by email, event, or user ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="date-range">
            <input
              type="date"
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value ? new Date(e.target.value) : null }))}
              className="date-input"
            />
            <span>to</span>
            <input
              type="date"
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value ? new Date(e.target.value) : null }))}
              className="date-input"
            />
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading logs...</div>
      ) : (
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Event</th>
                <th>Email</th>
                <th>User ID</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => (
                <tr 
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  className="log-row"
                >
                  <td>{formatDate(log.timestamp)}</td>
                  <td>{log.event}</td>
                  <td>{log.email}</td>
                  <td>{log.userId || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${log.error ? 'error' : 'success'}`}>
                      {log.error ? 'Error' : 'Success'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedLog && (
        <div className="log-details-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Log Details</h2>
              <button onClick={() => setSelectedLog(null)} className="close-button">×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Timestamp:</span>
                <span className="detail-value">{formatDate(selectedLog.timestamp)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Event:</span>
                <span className="detail-value">{selectedLog.event}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{selectedLog.email}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">User ID:</span>
                <span className="detail-value">{selectedLog.userId || 'N/A'}</span>
              </div>
              {selectedLog.error && (
                <div className="detail-row">
                  <span className="detail-label">Error:</span>
                  <span className="detail-value error">{selectedLog.error}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Details:</span>
                <pre className="detail-value details-json">
                  {formatDetails(selectedLog.details)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthLogs; 